import { FC, ReactNode, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Input, Svg, Typography } from '@altermaind/web/ui';
import { Claims } from '@/_shared/constants';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useDebounce } from '@/_shared/hooks/useDebounce';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { ClaimsComponent } from '../Claims';

type Props = {
  title?: string;
  description?: string;
  searchLabel?: string;
  onSearchCallback?: (value: string) => void;
  buttonAddOnClick?: () => void;
  isButtonAddDisabledOnTabletOrDesktop?: boolean;
  onAddClaimKey?: Claims | undefined;
  filtersComponent?: ReactNode;
  activeFiltersCount?: number;
  ['data-testid']?: string;
};

export const TableHeader: FC<Props> = ({
  title,
  description,
  searchLabel,
  onSearchCallback,
  buttonAddOnClick,
  isButtonAddDisabledOnTabletOrDesktop = false,
  onAddClaimKey,
  filtersComponent,
  activeFiltersCount,
  'data-testid': dataTestid = 'delegate-button',
}) => {
  const t = useTranslations('tableHeader');

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isMobile } = useBreakingPoints();

  const {
    isOpen: isOpenFilter,
    closeDialog: closeDialogFilter,
    openDialog: openDialogFilter,
  } = useDialog('filters');

  const debouncedSearch = useDebounce((value: string) => {
    onSearchCallback?.(value);
  }, 400);

  useEffect(() => {
    if (!isOpenFilter) return;
    const handleClick = (event: MouseEvent) => {
      const filterBox = document.getElementById('table-header-filter-box');
      if (filterBox && !filterBox.contains(event.target as Node)) {
        closeDialogFilter();
      }
    };

    document.addEventListener('mousedown', handleClick);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [closeDialogFilter, isOpenFilter]);

  return (
    <Box
      flexDirection={genericResponsiveValue({
        breakPoints: { desktop: 'row' },
        defaultValue: 'column',
      })}
      display='flex'
      justifyContent='space-between'
      alignItems={genericResponsiveValue({
        breakPoints: { desktop: 'center' },
        defaultValue: 'flex-end',
      })}
      mb='s'
    >
      {title || description ? (
        <Box
          display='flex'
          flexDirection='column'
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 's',
          })}
          width={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: '100%',
          })}
        >
          {title && (
            <Box display='flex' alignItems='center'>
              <Typography variant='h3' flex='1'>
                {title}
              </Typography>
              {isMobile && buttonAddOnClick && (
                <ClaimsComponent claimKey={onAddClaimKey}>
                  <Button
                    variant='positive'
                    onClick={buttonAddOnClick}
                    ml='xs'
                    px='xs'
                    minWidth='45px'
                    data-testid={`add-${dataTestid}`}
                    fontSize='xl'
                    disabled={isButtonAddDisabledOnTabletOrDesktop}
                  >
                    {t('add')}
                  </Button>
                </ClaimsComponent>
              )}
            </Box>
          )}
          {description && (
            <Typography variant='p2/light' color='grey/light'>
              {description}
            </Typography>
          )}
        </Box>
      ) : (
        <Box flex='1' />
      )}
      <Box display='flex' alignItems='center'>
        {onSearchCallback && (
          <Input
            suffixSvg='search'
            svgStyle={{
              fill: 'grey/light',
            }}
            inputTextStyle={{ pr: 'l' }}
            type='text'
            placeholder={searchLabel || t('search')}
            onChange={e => debouncedSearch(e.target.value)}
          />
        )}
        {filtersComponent && (
          <Box id='table-header-filter-box' position='relative' zIndex={1000}>
            <Button
              data-testid='filter-delegate-button'
              ml='xs'
              onClick={() => (isOpenFilter ? closeDialogFilter() : openDialogFilter())}
              type='button'
              backgroundColor='grey/dark/background'
              position='relative'
            >
              <Svg name='filter' fill='primary/text' size='xs' />
              <Typography as='span' variant='p2/light' ml='xxxs'>
                {t('filter')}
              </Typography>
              {activeFiltersCount && activeFiltersCount > 0 ? (
                <Box
                  position='absolute'
                  top='-8px'
                  right='-8px'
                  backgroundColor='white'
                  borderRadius='50%'
                  width='20px'
                  height='20px'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Typography variant='p2/light' fontSize='xxs' color='primary'>
                    {activeFiltersCount}
                  </Typography>
                </Box>
              ) : null}
            </Button>
            <Card
              overflow='visible'
              display={isOpenFilter ? 'block' : 'none'}
              right='0'
              top='xxl'
              backgroundColor='grey/dark/background'
              position='absolute'
              width={isMobile ? '240px' : '300px'}
            >
              {filtersComponent}
            </Card>
          </Box>
        )}
        {buttonAddOnClick && !isMobile && (
          <ClaimsComponent claimKey={onAddClaimKey}>
            <Button
              variant='positive'
              onClick={buttonAddOnClick}
              ml='xs'
              px='xs'
              minWidth='45px'
              data-testid={`add-${dataTestid}`}
              fontSize='xl'
            >
              {t('add')}
            </Button>
          </ClaimsComponent>
        )}
      </Box>
    </Box>
  );
};
